import { Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core';
import { DropdownItem } from '@frk/eds-components';
import { LiteratureDataService } from '@literature/services/literature-data.service';
import {
  PortfolioAllocation,
  PortfolioAllocationDataPoint,
  PortfolioHedgeManagerAllocations,
  PortfolioHolding,
  PortfolioTopHoldingTable,
  PortfolioTopTenHoldingMoneyFunds,
  Product,
} from '@models';
import { GraphQLFundDataService } from '@products/services/graphql-fund-data.service';
import { MapperFactory } from '@products/utils/mappers/mapper-factory';
import {
  getFirstNonEmptyProp,
  hasValues,
} from '@products/utils/mappers/mapper.utils';
import { HoldingsMapper } from '@products/utils/mappers/portfolio/holdings.mapper';
import { TopHoldingsTableMapper } from '@products/utils/mappers/portfolio/topholdings.mapper';
import { ProductMapper } from '@products/utils/mappers/product.type.mapper';
import { MapperParams } from '@products/utils/mappers/type.mapper';
import { DebugService, ProfileService, SiteConfigService } from '@services';
import { TranslateService } from '@shared/translate/translate.service';
import {
  BarChartData,
  ConfigurationId,
  FundId,
  FundUmbrella,
  LiteratureDocType,
  LiteratureDocument,
  PortfolioBaseType,
  PortfolioComponentId,
  PortfolioDataResponse,
  ProductDTO,
  ShareClassCode,
  StatisticsAndPositionsData,
  SuppressionSet,
} from '@types';
import { API_DATE_FORMAT } from '@utils/app.constants';
import { Logger } from '@utils/logger';
import { createDateComparator, floatComparator } from '@utils/sort-utils';
import { Apollo } from 'apollo-angular';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, publishLast, refCount, take } from 'rxjs/operators';
import { PortfolioComponentConfig } from '../portfolio-component.config';
import { PortfolioHoldingData } from '../portfolio-holdings/portfolio-holdings.component';
import { cloneDeep } from '@apollo/client/utilities';
import dayjs from 'dayjs';

const logger = Logger.getLogger('PortfolioService');

const isValidChartData = (point: PortfolioAllocationDataPoint): boolean =>
  point?.breakdownStd !== 0;

const createBaseFimesKey = (
  baseLabelKey: string,
  allocationFlag?: string,
  hasDerivatives?: boolean,
  hasCurrency?: boolean
): string | null => {
  let base = '';

  // NB: specifically test for true/false, as shouldn't append if null or undefined
  if (hasDerivatives === true) {
    base += '-derivatives-yes';
  }
  if (hasDerivatives === false) {
    base += '-derivatives-no';
  }

  if (hasCurrency === true) {
    base += '-cash-yes';
  }
  if (hasCurrency === false) {
    base += '-cash-no';
  }

  // e.g. '-calc-basis-mv'
  if (allocationFlag) {
    base += '-calc-basis-' + allocationFlag.toLowerCase();
  }

  // if string is empty at this point, return null;
  if (!base) {
    return null;
  }

  // prepend with base label
  return `${baseLabelKey}-with${base}`;
};

interface PortfolioCache {
  [fundId: string]: {
    [componentId: string]: Observable<PortfolioDataResponse>;
  };
}

type PortfolioHoldingTable = PortfolioHolding | PortfolioTopHoldingTable;

@Injectable({
  providedIn: 'root',
})
export class PortfolioService extends GraphQLFundDataService {
  private portfolioSelectionSubject$: Subject<void> = new Subject<void>();
  private cache: PortfolioCache = {};
  private showHistAvgeLifeMaturity = false;

  constructor(
    apollo: Apollo,
    siteConfigService: SiteConfigService,
    private translateService: TranslateService,
    private debugService: DebugService,
    private mapperFactory: MapperFactory,
    private literatureDataService: LiteratureDataService,
    private profileService: ProfileService
  ) {
    super(apollo, siteConfigService);
  }

  /**
   * Return required variables value to graphql query.
   * @param fundDataServiceParams params required for graphql query
   */
  private getVariables(fundId: FundId): Record<string, any> {
    return {
      countrycode: this.getCountry(),
      languagecode: this.getLanguage(),
      fundid: fundId,
    };
  }

  private createTooltip(
    labelKey: string,
    fimesLabelKey: string,
    isShowLabelKeys: boolean
  ): string {
    if (isShowLabelKeys) {
      let debugString = '';
      if (labelKey) {
        debugString += `[${labelKey}-tooltip]`;
      }
      if (labelKey && fimesLabelKey) {
        debugString += '<br />';
      }
      if (fimesLabelKey) {
        debugString += `[${fimesLabelKey}-tooltip]`;
      }
      return debugString;
    }

    const primaryTooltip: string = this.translateService.tooltip(labelKey);
    logger.debug('primaryTooltip:', labelKey, primaryTooltip);
    const secondaryTooltip: string = this.translateService.tooltip(
      fimesLabelKey
    );
    logger.debug('secondaryTooltip:', fimesLabelKey, secondaryTooltip);

    let tooltip = '';
    if (primaryTooltip) {
      tooltip += `<p>${primaryTooltip}</p>`;
    }
    if (secondaryTooltip) {
      tooltip += `<p>${secondaryTooltip}</p>`;
    }
    return tooltip;
  }

  private mapAllocationToChart(
    allocation: PortfolioAllocation,
    fundId: FundId,
    isShowLabelKeys: boolean,
    isPercent = true
  ): BarChartData {
    const baseFimesKey = createBaseFimesKey(
      allocation.baseLabelKey,
      allocation.allocationFlag,
      allocation.hasDerivatives,
      allocation.hasCurrency
    );
    const tooltipKey = `products.portfolio-${allocation.baseLabelKey}-heading`;
    const fimesTooltipKey = baseFimesKey
      ? `products.portfolio-${baseFimesKey}-heading`
      : null;
    return {
      fundId,
      chartTitle:
        allocation.allocationHeading ||
        this.translateService.instant(
          `products.portfolio-${allocation.baseLabelKey}-heading`
        ),
      caveatPlacement: `${
        allocation.caveatComponentId || allocation.componentId
      }:portfolio-${allocation.baseLabelKey}-caveat`,
      secondaryCaveatPlacement: baseFimesKey
        ? `${
            allocation.caveatComponentId || allocation.componentId
          }:portfolio-${baseFimesKey}-caveat`
        : null,
      tooltip: this.createTooltip(tooltipKey, fimesTooltipKey, isShowLabelKeys),
      asOfLabel: this.translateService.instant(`products.as-of`),
      asOfDate: allocation.asOfDate,
      breakdownColumnTitle: allocation.breakdownColumnTitle,
      contributionDateRange: allocation.contributionDateRange,
      calculationBasis: allocation.calculationBasis,
      calculationType: allocation.calculationType,
      componentId: allocation.componentId,
      benchmarkName: allocation.benchmarkName,
      updateFrequency:
        allocation.updateFrequency &&
        this.translateService.instant(
          `products.updated-${allocation.updateFrequency}`
        ),
      isPercent,
      hideChart: allocation.hideChart, // used by K2 Sector Exposure to hide chart (i.e. display table only)
      // hideTable: boolean; // not currently used?
      showExposureChart: allocation.showExposureChart, // used by exposure type charts
      showTotalPercentageBar: typeof allocation.totalPercent === 'number', // 0 is a valid value but will be falsy, so check type instead
      hideTableLegend: allocation.showExposureChart, // used by exposure type charts and Sector Exposure table
      hideBreakdownCol: !hasValues(allocation.data, 'breakdownStd'),
      // this column is affected by SiteConfigService's portfolio benchmark suppression setting
      showBenchmarkCol:
        !this.siteConfigService.isPortfolioBenchmarkSuppressed(fundId) &&
        hasValues(allocation.data, 'benchmark'),
      showDurationCol: hasValues(allocation.data, 'duration'),
      showGrossExposureCol: hasValues(allocation.data, 'grossExposure'),
      showLongExposureCol: hasValues(allocation.data, 'longExposure'),
      showNetExposureCol: hasValues(allocation.data, 'netExposure'),
      showShortExposureCol: hasValues(allocation.data, 'shortExposure'),
      showTargetAllocationCol: hasValues(allocation.data, 'targetAllocation'),
      showActualAllocationCol: hasValues(allocation.data, 'actualAllocation'),
      showRiskContributionAllocationCol: hasValues(
        allocation.data,
        'riskContributionAllocation'
      ),
      showPerformanceContributionAllocationCol: hasValues(
        allocation.data,
        'performanceContributionAllocation'
      ),
      proximalTopPlacement: `${allocation.baseLabelKey}-proximal-top`,
      proximalBottomPlacement: `${allocation.baseLabelKey}-proximal-bottom`,
      secondaryProximalTopPlacement: baseFimesKey
        ? `${baseFimesKey}-proximal-top`
        : null,
      secondaryProximalBottomPlacement: baseFimesKey
        ? `${baseFimesKey}-proximal-bottom`
        : null,
      dataPoints: allocation.data,
      totalPercent: allocation.totalPercent,
    };
  }

  public getDownloadUrl(
    fundId: FundId,
    shareClassCode: ShareClassCode,
    fundUmbrella: FundUmbrella
  ): Observable<LiteratureDocument> {
    const paramMap = new Map();
    paramMap
      .set('cache', 'true')
      .set('frkOneTISNumber', fundId)
      .set('shareClass', shareClassCode);
    if (this.siteConfigService.isSiteInternational()) {
      paramMap.set('fundUmbrella', fundUmbrella);
    }
    return this.literatureDataService.loadData(paramMap, true, true).pipe(
      map((litData) => {
        const portfolioHoldingData = litData.document
          .filter(
            (data) => data.dctermsType === LiteratureDocType.PORTFOLIO_HOLDINGS
          )
          .sort((documentA, documentB) => {
            return dayjs(documentA.publicationDate).isBefore(
              dayjs(documentB.publicationDate)
            )
              ? 1
              : -1;
          })[0];
        let portfolioMappedData: LiteratureDocument = {};
        if (portfolioHoldingData) {
          portfolioMappedData = this.literatureDataService.literatureMapper(
            portfolioHoldingData
          );
          this.literatureDataService.mapLiteratureUrl(portfolioMappedData);
        }
        return portfolioMappedData;
      })
    );
  }

  public getPortfolioData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    logger.debug('cache getPortfolioData$() for ', config.componentId);
    // try to get data observable from cache
    let data$: Observable<PortfolioDataResponse> = this.cache[fundId]?.[
      config.componentId
    ];
    logger.debug('cache data found', data$);

    if (!data$) {
      // not in cache, so get fresh observable
      switch (config.baseType) {
        case PortfolioBaseType.PORTFOLIO_CHART:
          data$ = this.getPortfolioChartData$(config, fundId);
          break;
        case PortfolioBaseType.STATISTICS_AND_POSITIONS:
          data$ = this.getPortfolioStatisticsData$(config, fundId);
          break;
        case PortfolioBaseType.HOLDINGS:
          data$ = this.getPortfolioHoldingsData$(config, fundId);
          break;
        case PortfolioBaseType.MANAGER_ROSTER_ALLOCATION:
          data$ = this.getPortfolioManagerRosterData$(config, fundId);
          break;
        default:
          data$ = null;
      }
      logger.debug('cache get fresh data', data$);

      // set in cache, and share so doesn't make duplicate gql http calls
      if (!this.cache[fundId]) {
        this.cache[fundId] = {};
      }
      this.cache[fundId][config.componentId] = data$.pipe(
        take(1),
        publishLast(),
        refCount()
      );
    }

    logger.debug('cache contents', this.cache);
    return this.cache[fundId][config.componentId];
  }

  public getPortfolioStatisticsData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    // 1. use combineLatest so result will be recalculated when debug mode toggled
    return combineLatest([
      // 2. get GraphQL fragment from component config and call register() on GraphQLFundDataService to get data
      super.register(config.gqlQuery, this.getVariables(fundId)),
      this.debugService.isPortfolioDebug$(),
      this.debugService.isShowLabelKeys$(),
      this.profileService.isLoggedIn$(),
    ]).pipe(
      map(
        ([productDetail, isDebugMode, isShowLabelKeys, isLoggedIn]: [
          ApolloQueryResult<{ Portfolio: ProductDTO }>,
          boolean,
          boolean,
          boolean
        ]) => {
          logger.debug('stats data from graphql', productDetail);
          // 3. Get data for this component and create MapperParams
          const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            includeSoftClosed: true,
            translateService: this.translateService,
            fundId,
            isDebugMode,
            isShowLabelKeys,
          };

          // 4. map Product to get AssetClass
          const product: Product = this.mapperFactory
            .createMapper(ProductMapper, mapperParams)
            .toDomain(productDto);
          logger.debug('product', product, productDto);

          // 5. check config to see if component is suppressed
          const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
            config.componentId,
            fundId,
            product.assetClass
          );

          // 6. return early if component has been suppressed
          if (suppressionSet) {
            return {
              success: false,
              message: `Component "${config.componentId}" suppressed by Suppression Set "${suppressionSet.id}"`,
            };
          }

          // 7. map allocationDTO to StatisticsAndPositionsData object
          const statsData: StatisticsAndPositionsData = this.mapperFactory
            .createMapper(config.mapper, mapperParams)
            .toDomain(productDto, isLoggedIn);
          logger.debug('statsData', statsData, productDto);

          // 8. return early if component has no data
          if (!statsData) {
            return {
              success: false,
              message: `Component "${config.componentId}" has no data`,
            };
          }

          // 9. return success response with StatisticsAndPositionsData
          return {
            success: true,
            payload: statsData,
          };
        }
      )
    );
  }

  /**
   * Return portfolio holdings data.
   * @param config component config from blr
   * @param fundId target fund id
   */
  public getPortfolioHoldingsData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    // 1. use combineLatest so result will be recalculated when debug mode toggled
    return combineLatest([
      // 2. get GraphQL fragment from component config and call register() on GraphQLFundDataService to get data
      super.register(config.gqlQuery, this.getVariables(fundId)),
      this.debugService.isPortfolioDebug$(),
    ]).pipe(
      map(
        ([productDetail, isDebugMode]: [
          ApolloQueryResult<{ Portfolio: ProductDTO }>,
          boolean
        ]) => {
          logger.debug('holdings data from graphql', productDetail);
          // 3. Get data for this component and create MapperParams
          const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            includeSoftClosed: true,
            translateService: this.translateService,
            fundId,
            isDebugMode,
          };

          // 4. map Product to get AssetClass
          const product: Product = this.mapperFactory
            .createMapper(ProductMapper, mapperParams)
            .toDomain(productDto);
          logger.debug('product', product, productDto);

          // 5. check config to see if component is suppressed
          const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
            config.componentId,
            fundId,
            product.assetClass
          );

          // 6. return early if component has been suppressed
          if (suppressionSet) {
            return {
              success: false,
              message: `Component "${config.componentId}" suppressed by Suppression Set "${suppressionSet.id}"`,
            };
          }

          let portfolioHoldingData: PortfolioHoldingData;

          // Implement holdings data to table as per product type received.
          if (
            product.productType === 'Money Market Funds' &&
            this.siteConfigService.isSiteInternational() &&
            productDto?.portfolio?.toptenholdingsmoneyfunds?.length
          ) {
            // 7. map toptenholdingsmoneyfunds dto to PortfolioTopTenHoldingMoneyFunds object array.
            let holdings: PortfolioTopTenHoldingMoneyFunds[] = this.mapperFactory
              .createMapper(config.mapper, mapperParams)
              .toDomain(productDto.portfolio.toptenholdingsmoneyfunds);
            logger.debug('holdingsData', holdings, productDto);

            // 8. return early if component has no data
            if (!holdings || holdings.length === 0) {
              return {
                success: false,
                message: `Component "${config.componentId}" has no data`,
              };
            }

            // sort the holdings data as per hodings std value in desc order.
            holdings = holdings.sort((a, b) =>
              floatComparator(
                b.holdingValueStd,
                a.holdingValueStd,
                null,
                null,
                true
              )
            );

            const updateFrequency: string = getFirstNonEmptyProp(
              holdings,
              'updateFrequency'
            );
            portfolioHoldingData = {
              fundId,
              assetClass: product.assetClass,
              fundName: product.fundName,
              title: this.translateService.instant(
                'products.portfolio-top-ten-holdings-money-fund-heading'
              ),
              caveatPlacement: `${config.componentId}:${config.baseLabelKey}-caveat`,
              proximalTop: `${config.baseLabelKey}-proximal-top`,
              proximalBottom: `${config.baseLabelKey}-proximal-bottom`,
              tooltip: this.translateService.tooltip(
                `products.portfolio-${config.baseLabelKey}-heading`
              ),
              asOfLabel: this.translateService.instant('products.as-of'),
              asOfDate: getFirstNonEmptyProp(holdings, 'asOfDate'),
              calculationBasis: '',
              calculationType: '',
              benchmarkName: '',
              updateFrequency:
                updateFrequency &&
                this.translateService.instant(
                  `products.updated-${updateFrequency}`
                ),
              productType: product.productType as ConfigurationId,
              holdings,
              isTopTenHoldingsMoneyFunds: true,
            };
          } else {
            const isTopHoldingData: boolean =
              productDto.portfolio?.dailyholdings?.length === 0 &&
              productDto.portfolio?.fullholdings?.length === 0;

            // 7. map holdings dto to PortfolioHolding or PortfolioTopHoldingTable object array.
            let holdings: PortfolioHolding[] | PortfolioTopHoldingTable[] = [];
            if (isTopHoldingData) {
              holdings = this.mapperFactory
                .createMapper(TopHoldingsTableMapper, mapperParams)
                .toDomain(productDto.portfolio?.topholdings);
            } else {
              holdings = this.mapperFactory
                .createMapper(HoldingsMapper, mapperParams)
                .toDomain(
                  // *** LOGIC: AT A TIME, THERE WOULD BE ONLY ONE OF DAILY/FULL HOLDINGS IN A FEED,
                  // SO HAVE A NON-EMPTY ONE FOR DISPLAY. ***
                  productDto.portfolio?.dailyholdings?.length > 0
                    ? productDto.portfolio.dailyholdings
                    : productDto.portfolio.fullholdings
                );
            }
            logger.debug('holdingsData', holdings, productDto);

            // 8. return early if component has no data
            if (!holdings || holdings.length === 0) {
              return {
                success: false,
                message: `Component "${config.componentId}" has no data`,
              };
            }

            // sort the holdings data as per weight in desc order. Ref. NGC-615.
            if (isTopHoldingData) {
              holdings = (holdings as PortfolioTopHoldingTable[]).sort((a, b) =>
                floatComparator(
                  b.breakdownPercentage,
                  a.breakdownPercentage,
                  null,
                  null,
                  true
                )
              );
            } else {
              holdings = (holdings as PortfolioHolding[]).sort((a, b) =>
                floatComparator(
                  b.percentageOfNetAssets,
                  a.percentageOfNetAssets,
                  null,
                  null,
                  true
                )
              );
            }

            const updateFrequency: string = getFirstNonEmptyProp(
              holdings,
              'updateFrequency'
            );
            const baseFimesKey = createBaseFimesKey(
              config.baseLabelKey,
              getFirstNonEmptyProp(holdings, 'allocationFlag'),
              getFirstNonEmptyProp(holdings, 'hasDerivatives')
            );

            const asOfDateData = this.getUniqueAsOfDates(holdings).sort(
              (a, b) => {
                const dateCompare = createDateComparator(API_DATE_FORMAT);
                return dateCompare(b.value, a.value, null, null, false);
              }
            );

            portfolioHoldingData = {
              fundId,
              assetClass: product.assetClass,
              fundName: product.fundName,
              title: isTopHoldingData
                ? this.translateService.instant(
                    'products.portfolio-top-ten-holdings-heading'
                  )
                : this.translateService.instant('products.portfolio-holdings'),
              caveatPlacement: `${config.componentId}:${config.baseLabelKey}-caveat`,
              secondaryCaveatPlacement: baseFimesKey
                ? `${config.componentId}:${baseFimesKey}-caveat`
                : null,
              proximalTop: `${config.baseLabelKey}-proximal-top`,
              secondaryProximalTop: baseFimesKey
                ? `${baseFimesKey}-proximal-top`
                : null,
              proximalBottom: `${config.baseLabelKey}-proximal-bottom`,
              secondaryProximalBottom: baseFimesKey
                ? `${baseFimesKey}-proximal-bottom`
                : null,
              tooltip: this.translateService.tooltip(
                `products.portfolio-${config.baseLabelKey}-heading`
              ),
              asOfLabel: this.translateService.instant('products.as-of'),
              asOfDate: getFirstNonEmptyProp(holdings, 'asOfDate'),
              calculationBasis: isTopHoldingData
                ? (holdings[0] as PortfolioTopHoldingTable).calculationBasis
                : '',
              calculationType: isTopHoldingData
                ? (holdings[0] as PortfolioTopHoldingTable).calculationType
                : '',
              updateFrequency:
                updateFrequency &&
                this.translateService.instant(
                  `products.updated-${updateFrequency}`
                ),
              productType: product.productType as ConfigurationId,
              holdings,
              hideColumns: this.selectIdentifier(
                this.getColListWithNullData(holdings),
                fundId
              ),
              isTopHoldingData,
              isTopTenHoldingsMoneyFunds: false,
              filteredAsOfDate: asOfDateData,
            };
            logger.debug('portfolioHoldingData', portfolioHoldingData);
          }

          // 9. return success response with PortfolioHoldingData
          return {
            success: true,
            payload: portfolioHoldingData,
          };
        }
      )
    );
  }

  /**
   * Filter based on asOfDateStd
   */
  private getUniqueAsOfDates(
    holdings: PortfolioHoldingTable[]
  ): DropdownItem[] {
    const sortedStdDate: string[] = [];
    const sortedDate: string[] = [];
    holdings.forEach((arrayItem) => {
      sortedStdDate.push(arrayItem.asOfDateStd);
      sortedDate.push(arrayItem.asOfDate);
    });
    const uniqueArrStdDate: string[] = [...new Set(sortedStdDate)];
    const uniqueArrDate: string[] = [...new Set(sortedDate)];
    const zip = (a1, a2) =>
      a1.map((x, i) => {
        return {
          label: x,
          value: a2[i],
        };
      });
    return zip(uniqueArrDate, uniqueArrStdDate);
  }

  /**
   * Filter columns names based upon null data in the same.
   * @param holdings portfolio holdings daily/full/top
   */
  private getColListWithNullData(holdings: PortfolioHoldingTable[]): string[] {
    const hideCols: string[] = [];
    const allColumns: Array<keyof PortfolioHoldingTable> = holdings[0]
      ? (Object.keys(holdings[0]) as Array<keyof PortfolioHoldingTable>)
      : [];

    allColumns.forEach((column) => {
      // NGC-7048: Hide cols with all EMDASH values too.
      if (!hasValues(holdings, column)) {
        hideCols.push(column);
      }
    });

    return hideCols;
  }

  /**
   * show ISIN or CUSIP based on configuration
   */
  private selectIdentifier(hideColumns: string[], fundId: FundId): string[] {
    if (
      this.siteConfigService.product.portfolio?.holdingsIdentifierOverride?.includes(
        fundId
      )
    ) {
      hideColumns.push('isinSecurityNumber');
    } else {
      hideColumns.push('cusipNumber');
    }
    return hideColumns;
  }

  public getPortfolioChartData$(
    portfolioComponentConfig: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    logger.debug(
      'getPortfolioChartData$() start for ',
      portfolioComponentConfig.componentId
    );
    // 1. get GraphQL query from component config and call register() on GraphQLFundDataService to get data
    return combineLatest([
      super.register(
        portfolioComponentConfig.gqlQuery,
        this.getVariables(fundId)
      ),
      this.debugService.isShowLabelKeys$(),
    ]).pipe(
      map(
        ([productDetail, isShowLabelKeys]: [
          ApolloQueryResult<{ Portfolio: ProductDTO }>,
          boolean
        ]) => {
          logger.debug(
            'getPortfolioChartData$() map data for',
            portfolioComponentConfig.componentId
          );

          // 2. Get data for this component and create MapperParams
          const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
          const mapperParams: MapperParams = {
            config: this.siteConfigService,
            translateService: this.translateService,
            includeSoftClosed: true,
          };

          // 3. map Product to get AssetClass
          const product: Product = this.mapperFactory
            .createMapper(ProductMapper, mapperParams)
            .toDomain(productDto);
          logger.debug('product', product, productDto);

          // 4. map allocationDTO to PortfolioAllocation object
          const allocations: PortfolioAllocation[] = this.mapperFactory
            .createMapper(portfolioComponentConfig.mapper, mapperParams)
            .toDomain(productDto.portfolio);
          logger.debug('allocations', allocations, productDto);

          // 5. return early if chart has no data
          if (!allocations || allocations.length === 0) {
            return {
              success: false,
              message: `Component "${portfolioComponentConfig.componentId}" has no data`,
            };
          }

          // 4. process allocations
          const payload: BarChartData[] = [];
          let message = '';
          allocations.forEach((allocation: PortfolioAllocation): void => {
            // 5. check config to see if chart suppressed from BR
            const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
              allocation.componentId,
              fundId,
              product.assetClass
            );
            if (suppressionSet) {
              // 6. Suppressed, so add to debug message
              message += `Component "${allocation.componentId}" suppressed by Suppression Set "${suppressionSet.id}"\n`;
            } else {
              // 7. Not suppressed, so map data and add to payload

              this.generatePayload(
                allocation,
                payload,
                fundId,
                isShowLabelKeys
              );
            }
          });

          // 8. return data
          return {
            success: payload.length > 0,
            payload,
            message,
          };
        }
      )
    );
  }

  // create payload data for chart
  private generatePayload(
    allocation: PortfolioAllocation,
    payload: BarChartData[],
    fundId: FundId,
    isShowLabelKeys: boolean
  ): void {
    // for impact pillar check if chart data is correct
    [
      PortfolioComponentId.IMPACT_PILLAR,
      PortfolioComponentId.IMPACT_FOCUS,
    ].includes(allocation.componentId)
      ? this.createPayloadForImpactTabs(
          allocation,
          payload,
          fundId,
          isShowLabelKeys
        )
      : payload.push(
          this.mapAllocationToChart(allocation, fundId, isShowLabelKeys)
        );
  }

  // return data with valid values
  private getValidChartData(
    allocation: PortfolioAllocation
  ): PortfolioAllocation {
    const filteredData = allocation.data.filter(isValidChartData);
    allocation.data = cloneDeep(filteredData);

    return allocation;
  }

  // Added extra rule to remove particular data from chart if value is 0
  private createPayloadForImpactTabs(
    allocation: PortfolioAllocation,
    payload: BarChartData[],
    fundId: FundId,
    isShowLabelKeys: boolean
  ): void {
    const validChartAllocationData = this.getValidChartData(allocation);
    if (validChartAllocationData.data.length) {
      payload.push(
        this.mapAllocationToChart(
          validChartAllocationData,
          fundId,
          isShowLabelKeys,
          allocation.componentId === PortfolioComponentId.IMPACT_FOCUS
        )
      );
    }
  }

  public getPortfolioManagerRosterData$(
    config: PortfolioComponentConfig,
    fundId: FundId
  ): Observable<PortfolioDataResponse> {
    // 1. get GraphQL fragment from component config and call register() on GraphQLFundDataService to get data
    return super.register(config.gqlQuery, this.getVariables(fundId)).pipe(
      map((productDetail: ApolloQueryResult<{ Portfolio: ProductDTO }>) => {
        // 2. Get data for this component and create MapperParams
        const productDto: ProductDTO = productDetail?.data?.Portfolio || null;
        const mapperParams: MapperParams = {
          config: this.siteConfigService,
          translateService: this.translateService,
          includeSoftClosed: true,
        };

        // 3. map Product to get AssetClass
        const product: Product = this.mapperFactory
          .createMapper(ProductMapper, mapperParams)
          .toDomain(productDto);
        logger.debug('PortfolioManagerRosterData', product, productDto);

        // 4. check config to see if chart suppressed
        const suppressionSet: SuppressionSet = this.siteConfigService.getPortfolioComponentSuppressionSet(
          config.componentId,
          fundId,
          product.assetClass
        );

        // 5. return early if chart has been suppressed
        if (suppressionSet) {
          return {
            success: false,
            message: `Component "${config.componentId}" suppressed by Suppression Set "${suppressionSet.id}"`,
          };
        }

        // 6. map allocationDTO to PortfolioAllocation object
        const allocation: PortfolioHedgeManagerAllocations = this.mapperFactory
          .createMapper(config.mapper, mapperParams)
          .toDomain(productDto.portfolio);
        logger.debug('allocation', allocation, productDto);

        // 7. return early if chart has no data
        if (!allocation) {
          return {
            success: false,
            message: `Component "${config.componentId}" has no data`,
          };
        }

        // 8. return success (if datapoints) with PortfolioAllocation object mapped to PortfolioChartData object
        return {
          success: allocation.strategies.length > 0,
          payload: allocation,
        };
      })
    );
  }

  public get portfolioSelection$(): Observable<void> {
    return this.portfolioSelectionSubject$.asObservable();
  }

  public handlePortfolioTabSelection() {
    this.portfolioSelectionSubject$.next();
  }

  public getShowHistAvgeLifeMaturity(): boolean {
    return this.showHistAvgeLifeMaturity;
  }

  public setShowHistAvgeLifeMaturity(
    displayFlag: boolean,
    componentType: PortfolioComponentId
  ): void {
    if (
      componentType === PortfolioComponentId.PORTFOLIO_STATISTICS_AND_POSITIONS
    ) {
      this.showHistAvgeLifeMaturity = displayFlag;
    }
  }
}
